import { z } from 'zod';
import { insertListingSchema, insertMessageSchema, listings, messages, favorites, conversations, inventory } from './schema';

type Inventory = typeof inventory.$inferSelect;

export const errorSchemas = {
  validation: z.object({
    message: z.string(),
    field: z.string().optional(),
  }),
  notFound: z.object({
    message: z.string(),
  }),
  internal: z.object({
    message: z.string(),
  }),
  unauthorized: z.object({
    message: z.string(),
  }),
  forbidden: z.object({
    message: z.string(),
  }),
  badRequest: z.object({
    message: z.string(),
  }),
};

export const api = {
  listings: {
    list: {
      method: 'GET' as const,
      path: '/api/listings',
      input: z.object({
        search: z.string().optional(),
        category: z.string().optional(),
        subcategory: z.string().optional(),
        // Comma-separated canonical subcategory leaf names. Used by the
        // mobile drill-down "عرض كل" on a NESTED branch to filter results
        // to the same leaf set whose count was just shown on the badge.
        // Server expands legacy aliases, dedupes, and caps the list size.
        subcategories: z.string().optional(),
        // "1": only items of `category` that sit in no leaf of its tree
        // (empty or unknown subcategory) — the «بلا قسم فرعي» entry (#168).
        unclassified: z.string().optional(),
        minPrice: z.coerce.number().optional(),
        maxPrice: z.coerce.number().optional(),
        location: z.string().optional(),
        city: z.string().optional(),
        isPremium: z.coerce.boolean().optional(),
        condition: z.string().optional(),
        todayOnly: z.coerce.boolean().optional(),
        virtualStoreTodayOnly: z.coerce.boolean().optional(),
        fridayOnly: z.coerce.boolean().optional(),
        sellerType: z.string().optional(),
        wholesaleArea: z.string().optional(),
        wholesaleMarket: z.string().optional(),
        listingType: z.string().optional(),
        excludeWholesale: z.coerce.boolean().optional(),
        dropshippingOnly: z.coerce.boolean().optional(),
        dropshippingFalse: z.coerce.boolean().optional(),
        merchantDropshippingTodayOnly: z.coerce.boolean().optional(),
        district: z.string().optional(),
        sortBy: z.enum(["newest", "oldest", "price_low", "price_high", "most_viewed"]).optional(),
        page: z.coerce.number().min(1).default(1),
        limit: z.coerce.number().min(1).max(100).default(20),
        // ─── Vehicle-attribute filters (vasita category) ──────────────────
        // Values are Arabic strings as stored in listings.attributes JSONB.
        brand:   z.string().optional(), // e.g. "تويوتا"
        model:   z.string().optional(), // e.g. "كورولا"
        series:  z.string().optional(), // e.g. "GLI"  (trim / variant)
        minYear: z.coerce.number().int().min(1900).max(2100).optional(),
        maxYear: z.coerce.number().int().min(1900).max(2100).optional(),
        minKm:   z.coerce.number().int().min(0).optional(),
        maxKm:   z.coerce.number().int().min(0).optional(),
        fuelType: z.string().optional(), // e.g. "بنزين"
        gearType: z.string().optional(), // e.g. "أوتوماتيك"
      }).optional(),
      responses: {
        200: z.object({
          data: z.array(z.custom<typeof listings.$inferSelect>()),
          total: z.number(),
          page: z.number(),
          limit: z.number(),
        }),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/listings/:id',
      responses: {
        200: z.custom<typeof listings.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/listings',
      input: insertListingSchema.omit({ userId: true as any }),
      responses: {
        201: z.custom<typeof listings.$inferSelect>(),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/listings/:id',
      input: insertListingSchema.partial(),
      responses: {
        200: z.custom<typeof listings.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
        401: errorSchemas.unauthorized,
        403: errorSchemas.forbidden,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/listings/:id',
      responses: {
        204: z.void(),
        404: errorSchemas.notFound,
        401: errorSchemas.unauthorized,
        403: errorSchemas.forbidden,
      },
    },
    myListings: {
      method: 'GET' as const,
      path: '/api/my-listings',
      responses: {
        200: z.array(z.custom<typeof listings.$inferSelect>()),
        401: errorSchemas.unauthorized,
      },
    },
    userListings: {
      method: 'GET' as const,
      path: '/api/users/:userId/listings',
      responses: {
        200: z.array(z.custom<typeof listings.$inferSelect>()),
      },
    },
  },
  favorites: {
    list: {
      method: 'GET' as const,
      path: '/api/favorites',
      responses: {
        200: z.array(z.custom<typeof favorites.$inferSelect & { listing: typeof listings.$inferSelect }>()),
        401: errorSchemas.unauthorized,
      },
    },
    add: {
      method: 'POST' as const,
      path: '/api/favorites',
      input: z.object({ listingId: z.number() }),
      responses: {
        201: z.custom<typeof favorites.$inferSelect>(),
        401: errorSchemas.unauthorized,
      },
    },
    remove: {
      method: 'DELETE' as const,
      path: '/api/favorites/:listingId',
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
      },
    },
    check: {
      method: 'GET' as const,
      path: '/api/favorites/:listingId/check',
      responses: {
        200: z.object({ isFavorite: z.boolean() }),
      },
    },
  },
  messages: {
    conversations: {
      method: 'GET' as const,
      path: '/api/conversations',
      responses: {
        200: z.array(z.custom<typeof conversations.$inferSelect & { 
          listing: typeof listings.$inferSelect;
          otherUser: { id: string; firstName: string | null; lastName: string | null; profileImageUrl: string | null };
          lastMessage: typeof messages.$inferSelect | null;
          unreadCount: number;
        }>()),
        401: errorSchemas.unauthorized,
      },
    },
    getConversation: {
      method: 'GET' as const,
      path: '/api/conversations/:id',
      responses: {
        200: z.object({
          conversation: z.custom<typeof conversations.$inferSelect>(),
          messages: z.array(z.custom<typeof messages.$inferSelect>()),
          listing: z.custom<typeof listings.$inferSelect>(),
          otherUser: z.object({
            id: z.string(),
            firstName: z.string().nullable(),
            lastName: z.string().nullable(),
            profileImageUrl: z.string().nullable(),
          }),
        }),
        404: errorSchemas.notFound,
        401: errorSchemas.unauthorized,
        403: errorSchemas.forbidden,
      },
    },
    send: {
      method: 'POST' as const,
      path: '/api/messages',
      input: z.object({ conversationId: z.number(), content: z.string().min(1).max(1000) }),
      responses: {
        201: z.custom<typeof messages.$inferSelect>(),
        401: errorSchemas.unauthorized,
      },
    },
    startConversation: {
      method: 'POST' as const,
      path: '/api/conversations',
      input: z.object({ listingId: z.number(), message: z.string() }),
      responses: {
        201: z.object({
          conversation: z.custom<typeof conversations.$inferSelect>(),
          message: z.custom<typeof messages.$inferSelect>(),
        }),
        401: errorSchemas.unauthorized,
      },
    },
    markRead: {
      method: 'PUT' as const,
      path: '/api/conversations/:id/read',
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorSchemas.unauthorized,
      },
    },
    unreadCount: {
      method: 'GET' as const,
      path: '/api/messages/unread-count',
      responses: {
        200: z.object({ count: z.number() }),
        401: errorSchemas.unauthorized,
      },
    },
  },
  users: {
    profile: {
      method: 'GET' as const,
      path: '/api/users/:userId',
      responses: {
        200: z.object({
          id: z.string(),
          firstName: z.string().nullable(),
          lastName: z.string().nullable(),
          profileImageUrl: z.string().nullable(),
          createdAt: z.date().nullable(),
          listingsCount: z.number(),
        }),
        404: errorSchemas.notFound,
      },
    },
  },
  inventory: {
    list: {
      method: 'GET' as const,
      path: '/api/inventory',
      responses: {
        200: z.array(z.custom<Inventory>()),
        401: errorSchemas.unauthorized,
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/inventory/:id',
      responses: {
        200: z.custom<Inventory>(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    buy: {
      method: 'POST' as const,
      path: '/api/inventory/buy',
      input: z.object({
        listingId: z.number(),
      }),
      responses: {
        201: z.custom<Inventory>(),
        400: errorSchemas.badRequest,
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    listForSale: {
      method: 'POST' as const,
      path: '/api/inventory/:id/list',
      input: z.object({
        price: z.number().min(1),
      }),
      responses: {
        200: z.custom<Inventory>(),
        400: errorSchemas.badRequest,
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
  },
};

export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(String(value)));
    });
  }
  const remaining = url.match(/:[a-zA-Z]+/g);
  if (remaining?.length) {
    throw new Error(`Missing URL params: ${remaining.join(', ')}`);
  }
  return url;
}
